import { memo } from "react";

import { useTheme, Typography, ListItem, ListItemText, Skeleton } from "@mui/material";
import { FormatDateOptions, useIntl } from "react-intl";

import TitledCard from "./templates/basic/TitledCard";
import { useDevice } from "../utils/DeviceProvider";
import { DeviceTemplateItem } from "../utils/types";
import { reservedTemplateKeys } from "./templates/BasicTemplate";

const TemplateInfo = memo(() => {
  const {
    device: { template, config },
  } = useDevice();
  const { formatDate, formatMessage } = useIntl();

  const theme = useTheme();

  const dateFormatOptions: FormatDateOptions = {
    dateStyle: "short",
    timeStyle: "medium",
  };

  return (
    <TitledCard title={formatMessage({ defaultMessage: "Template info" })} stretch={true}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <ListItem sx={{ paddingY: 0 }}>
          <ListItemText
            primary={
              <Typography variant="body1" component="span" color="secondary.main">
                {formatMessage({ defaultMessage: "Template created" })}
              </Typography>
            }
            secondary={template ? formatDate(template.createdTs, dateFormatOptions) : <Skeleton variant="text" />}
          />
        </ListItem>

        <ListItem sx={{ paddingY: 0 }}>
          <ListItemText
            primary={
              <Typography variant="body1" component="span" color="secondary.main">
                {formatMessage({ defaultMessage: "Firmware version" })}
              </Typography>
            }
            secondary={template ? template.template?.info?.fw : <Skeleton variant="text" />}
          />
        </ListItem>

        <ListItem sx={{ paddingY: 0 }}>
          <ListItemText
            primary={
              <Typography
                variant="body1"
                component="span"
                color={
                  !(template?.template?.status?.configVersion && config?.toolboxConfig?.configVersion) ||
                  template?.template?.status?.configVersion === config?.toolboxConfig?.configVersion
                    ? theme.palette.secondary.main
                    : theme.palette.warning.main
                }
              >
                {formatMessage({ defaultMessage: "Active configuration" })}
              </Typography>
            }
            secondary={template ? template.template?.status?.configVersion || "-" : <Skeleton variant="text" />}
          />
        </ListItem>

        {template?.template &&
          Object.entries(template.template)
            .filter(([key, value]) => !reservedTemplateKeys.includes(key) && Object.keys(value).includes("version"))
            .map(([key, value]) => (
              <ListItem sx={{ paddingY: 0 }} key={`template-${key}`}>
                <ListItemText
                  primary={
                    <Typography variant="body1" component="span" color="secondary.main">
                      {key}
                    </Typography>
                  }
                  secondary={(value as DeviceTemplateItem).version}
                />
              </ListItem>
            ))}
      </div>
    </TitledCard>
  );
});

export default TemplateInfo;
